/* eslint-disable react-hooks/set-state-in-effect, @typescript-eslint/no-explicit-any, react-hooks/exhaustive-deps */
import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import ThemeToggle from "./ThemeToggle";
import { useSession, useAuth } from "../../context/AuthContext";
import { APP_NAME } from "../../lib/constants";
import api from "../../services/api";

export default function Header() {
  const [firstWord, ...restWords] = APP_NAME.split(" ");
  const restOfName = restWords.join(" ");
  const { data: session }: any = useSession();
  const { logout } = useAuth();
  const userId = session?.user?.id;
  const role = session?.user?.role || 'STUDENT';
  const navigate = useNavigate();
  
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const checkStandalone = 
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as any).standalone === true;
      
      setIsStandalone(checkStandalone);
    }
  }, []);

  const fetchNotifications = async () => {
    if (!userId) return;
    try {
      const res = await api.get(`/notifications/${userId}?t=${Date.now()}`);
      setNotifications(res.data);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // 30s refresh for header
    return () => clearInterval(interval);
  }, [userId]);

  const handleMarkAllRead = async () => {
    if (!userId) return;
    try {
      await api.post(`/notifications/read-all`, { userId });
      setNotifications(notifications.map(n => ({ ...n, read: true })));
    } catch (err) {
      console.error("Failed to mark all read:", err);
    }
  };

  const handleNotificationClick = (n: any) => {
    if (n.metadata?.taskId) {
      const url = n.metadata.studentId 
        ? `/tasks/${n.metadata.taskId}?userId=${n.metadata.studentId}`
        : `/tasks/${n.metadata.taskId}`;
      navigate(url);
    }
    setIsNotificationsOpen(false);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false);
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <header className="fixed top-0 w-full z-50 flex flex-col bg-surface/70 backdrop-blur-xl border-b border-outline-variant/30">
      <div className="h-16 flex justify-between items-center px-6 w-full">
      <div className="flex items-center gap-4">
        <div 
          className="md:hidden cursor-pointer p-1 -ml-1 rounded-lg hover:bg-surface-container-high transition-colors" 
          onClick={() => window.dispatchEvent(new Event('toggleMobileSidebar'))}
        >
          <span className="material-symbols-outlined text-on-surface">menu</span>
        </div>
        <Link to="/dashboard" className="flex items-center gap-2 group">
          <div className="w-8 h-8 relative flex items-center justify-center">
            <img src="/academy_logo.png" alt="HitClicks Logo" className="w-full h-full object-contain" />
          </div>
          <span className="text-xl font-black text-on-surface tracking-tighter transition-colors">
            {firstWord} <span className="brand-gradient">{restOfName}</span>
          </span>
        </Link>
      </div>

      <div className="flex items-center gap-6">
        <div className="flex items-center gap-3">
          
          <ThemeToggle />

          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button 
              onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              className="p-2 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-full transition-colors relative"
            >
              <span className="material-symbols-outlined">notifications</span>
              {unreadCount > 0 && (
                <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full border-2 border-surface shadow-sm animate-pulse"></span>
              )}
            </button>
            
            {/* Notification Tray */}
            {isNotificationsOpen && (
              <div className="absolute right-0 top-full mt-2 w-80 bg-surface-container-lowest border border-outline-variant shadow-2xl rounded-2xl overflow-hidden animate-in fade-in slide-in-from-top-2 duration-300 z-[101]">
                <div className="p-4 border-b border-outline-variant flex justify-between items-center bg-surface-container-low">
                  <span className="text-[10px] font-black uppercase tracking-widest text-on-surface">Notifications</span>
                  {unreadCount > 0 && (
                    <button 
                      onClick={handleMarkAllRead}
                      className="text-[10px] font-bold text-primary hover:underline"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length > 0 ? notifications.map((n) => (
                    <div 
                      key={n.id}
                      onClick={() => handleNotificationClick(n)}
                      className={`p-4 flex gap-4 items-start hover:bg-primary/5 transition-colors cursor-pointer border-b border-outline-variant/30 ${!n.read ? "bg-primary/[0.02]" : "opacity-60"}`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        n.type === "COMMENT_NEW" ? "bg-blue-100 text-blue-600 dark:bg-blue-900/30 font-black" : "bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30"
                      }`}>
                        <span className="material-symbols-outlined text-sm">
                          {n.type === "COMMENT_NEW" ? "chat" : "verified"}
                        </span>
                      </div>
                      <div className="flex-1 space-y-0.5">
                        <p className="text-xs font-black text-on-surface">{n.title}</p>
                        <p className="text-[10px] font-medium text-on-surface-variant leading-relaxed line-clamp-2">{n.message}</p>
                        <p className="text-[9px] font-bold text-slate-400 mt-1 uppercase tracking-tight">
                          {new Date(n.created_at || n.createdAt || Date.now()).toLocaleDateString()}
                        </p>
                      </div>
                      {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />}
                    </div>
                  )) : (
                    <div className="p-10 text-center space-y-3 opacity-30">
                        <span className="material-symbols-outlined text-4xl">notifications_off</span>
                        <p className="text-[10px] font-black uppercase tracking-widest">Clear Horizons</p>
                    </div>
                  )}
                </div>
                <Link to="/notifications" className="block p-3 text-center text-[10px] font-black uppercase tracking-widest text-primary hover:bg-primary/5 transition-colors border-t border-outline-variant/30">
                  View All Notifications
                </Link>
              </div>
            )}
          </div>
          
          {/* User Menu Dropdown */}
          <div className="relative" ref={menuRef}>
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="h-8 w-8 rounded-full bg-surface-container-high overflow-hidden border border-outline-variant hover:ring-2 hover:ring-primary/20 transition-all active:scale-95 flex items-center justify-center"
            >
              {session?.user?.avatarUrl ? (
                <img 
                  src={session.user.avatarUrl} 
                  alt="User"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-[10px] font-black text-primary uppercase">
                  {session?.user?.name?.[0] || session?.user?.email?.[0] || "U"}
                </span>
              )}
            </button>

            {isMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-surface-container-lowest border border-outline-variant shadow-2xl rounded-xl overflow-hidden animate-in fade-in zoom-in-95 duration-200 z-[100]">
                <div className="py-2">
                  <Link 
                    to="/settings" 
                    className="flex items-center gap-3 px-4 py-3 text-sm text-on-surface hover:bg-primary/5 transition-colors group"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-slate-400 group-hover:text-primary">person</span>
                    User Profile
                  </Link>
                  <Link 
                    to="/profile/business" 
                    className="flex items-center gap-3 px-4 py-3 text-sm text-on-surface hover:bg-primary/5 transition-colors group"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="material-symbols-outlined text-slate-400 group-hover:text-primary">business_center</span>
                    Business Profile
                  </Link>
                  {!isStandalone && (
                    <button 
                      onClick={() => {
                        setIsMenuOpen(false);
                        
                        const globalPrompt = (window as any).deferredPrompt;
                        if (globalPrompt) {
                          try {
                            const promptResult = globalPrompt.prompt();
                            if (promptResult && typeof promptResult.catch === "function") {
                              promptResult.catch((err: any) => {
                                console.warn("PWA prompt rejection caught in header, falling back to banner:", err);
                                window.dispatchEvent(new CustomEvent("triggerPWAInstall"));
                              });
                            }
                            globalPrompt.userChoice.then((choiceResult: { outcome: string }) => {
                              if (choiceResult.outcome === "accepted") {
                                window.dispatchEvent(new CustomEvent("pwaInstalledSuccess"));
                              }
                            });
                          } catch (err) {
                            console.warn("Direct PWA prompt error from header, falling back to banner:", err);
                            window.dispatchEvent(new CustomEvent("triggerPWAInstall"));
                          }
                        } else {
                          window.dispatchEvent(new CustomEvent("triggerPWAInstall"));
                        }
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-on-surface hover:bg-primary/5 transition-colors group cursor-pointer text-left"
                    >
                      <span className="material-symbols-outlined text-slate-400 group-hover:text-primary">install_mobile</span>
                      Install App
                    </button>
                  )}
                  {role === 'ADMIN' && (
                    <button 
                      onClick={() => {
                        setIsMenuOpen(false);
                        navigate('/admin/users');
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors group cursor-pointer text-left"
                    >
                      <span className="material-symbols-outlined text-slate-400 group-hover:text-primary">shield_person</span>
                      Admin Panel
                    </button>
                  )}
                  <button 
                    onClick={() => {
                      setIsMenuOpen(false);
                      logout();
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors group cursor-pointer text-left border-t border-outline-variant/30 mt-1"
                  >
                    <span className="material-symbols-outlined text-slate-400 group-hover:text-red-600 dark:group-hover:text-red-400">logout</span>
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      </div>
    </header>
  );
}
